'use client';

/**
 * One-shot client capture of the wisprs_attr signup-attribution cookie.
 *
 * Drops into every free-tool tool page (and any marketing page where we want
 * attribution). Runs ONCE on mount. Idempotent — never overwrites a cookie
 * that's already present (write-once on the client).
 *
 * Normally middleware (`captureAttribution` in middleware.ts) has already
 * written a fully-classified cookie by the time this mounts, and this
 * component just tags `tool` onto it. But middleware SILENTLY skips writing
 * on a bot-UA false positive or an unusual Sec-Fetch-Dest profile (in-app
 * browsers, link-unfurl prefetches — see middleware.ts) — when that happens,
 * this component is the first thing that ever writes the cookie, so it has to
 * classify the touch the same way middleware does (buildAttributionFromRequest)
 * rather than just stamping `tool` with nothing else.
 *
 * A channel-less cookie stamps onto users.signup_source write-once and reads
 * as "—" forever in the admin (see acquisition.ts / admin/users/page.tsx),
 * indistinguishable at a glance from a genuine unclassifiable touch. Confirmed
 * against a real signup: hebronbaptistonline@gmail.com's signup_source was
 * exactly `{ tool: 'vimeo-to-mp3', capturedAt }` — no channel, no referrer, no
 * landing — which is only reachable through the old fallback below.
 *
 * The handoff page (and any other post-signup landing) later reads this cookie
 * and stamps users.signup_source via stampSignupSourceOnce (also write-once).
 */
import { useEffect } from 'react';

import {
  SIGNUP_ATTR_COOKIE,
  SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS,
  buildAttributionFromRequest,
  decodeAttrCookie,
} from '@/lib/analytics/signup-source-config';
import { encodeAttrCookie, type SignupSource } from '@/lib/analytics/signup-source-config';

function readAttrCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${SIGNUP_ATTR_COOKIE}=`));

  return match ? match.slice(SIGNUP_ATTR_COOKIE.length + 1) : null;
}

function writeAttrCookie(value: string): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SIGNUP_ATTR_COOKIE}=${value}; Max-Age=${SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

export function AttributionCapture({ tool }: { tool: string }) {
  useEffect(() => {
    const existing = decodeAttrCookie(readAttrCookie());

    // Acquisition source is write-once and must stay that way: overwriting it
    // here would make an organic visitor look like they came from our own tool
    // page. But `tool` used to be lost entirely whenever ANY cookie already
    // existed, so anyone who saw /pricing first (middleware writes the cookie
    // there) signed up with no tool attributed. Stamp `tool` when it is absent,
    // and leave every already-classified field exactly as it was found.
    if (existing?.tool) return;

    if (existing) {
      writeAttrCookie(encodeAttrCookie({ ...existing, tool }));

      return;
    }

    // No cookie at all — middleware's write missed this visitor. This is now
    // the writer of record, so classify the touch instead of leaving `channel`
    // unset the way the old fallback did.
    const capturedAt = new Date().toISOString();

    const base: SignupSource = buildAttributionFromRequest({
      searchParams: new URL(window.location.href).searchParams,
      referer: document.referrer || null,
      selfHost: window.location.host,
      landing: window.location.pathname,
      capturedAt,
    }) ?? {
      channel: 'direct',
      attributionScope: 'first-touch',
      sourceEvidence: 'no-referrer',
      capturedAt,
    };

    writeAttrCookie(encodeAttrCookie({ ...base, tool }));
  }, [tool]);

  return null;
}
